/* RFQ frontend state only. A browser return marker is not proof of email delivery. */
(function () {
  'use strict';
  if (window.mvpRfqSubmission) return;
  const ttl = 60 * 60 * 1000;
  const prefix = 'mvp-rfq-return-v1:';
  const latestPrefix = 'mvp-rfq-latest-v1:';
  const active = new WeakMap();
  const triggers = new WeakMap();
  const sheetAttempts = new Map();
  function id() {
    return window.crypto && window.crypto.randomUUID
      ? window.crypto.randomUUID()
      : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  }
  function storage(method, key, value) {
    try { return window.sessionStorage[method](key, value); } catch (_) { return null; }
  }
  function readReceipt(token) {
    try {
      const receipt = JSON.parse(storage('getItem', prefix + token) || 'null');
      if (!receipt || Date.now() - receipt.at > ttl || Date.now() < receipt.at || receipt.token !== token) {
        storage('removeItem', prefix + token);
        return null;
      }
      return receipt;
    } catch (_) { storage('removeItem', prefix + token); return null; }
  }
  function cleanExpiredReceipts() {
    try {
      Object.keys(window.sessionStorage).filter(key => key.startsWith(prefix))
        .forEach(key => readReceipt(key.slice(prefix.length)));
    } catch (_) {}
  }
  cleanExpiredReceipts();
  function release(form) {
    const state = active.get(form);
    if (!state) return;
    window.clearTimeout(state.timer);
    active.delete(form);
    form.removeAttribute('aria-busy');
    if (state.button) state.button.removeAttribute('aria-busy');
    if (state.onRelease) state.onRelease();
  }
  function beginNative(form, options) {
    if (!(form instanceof HTMLFormElement)) return null;
    const previous = active.get(form);
    // Shopify can replay a submit after attaching its CAPTCHA token. Keep the
    // populated native body and allow that replay; do not dispatch another Sheet POST.
    if (previous) return { repeat: true, receipt: previous.receipt };
    const returnField = form.elements.namedItem('return_to');
    let target;
    try { target = new URL((returnField && returnField.value) || window.location.href, window.location.href); }
    catch (_) { target = new URL(window.location.href); }
    if (target.origin !== window.location.origin) target = new URL(window.location.href);
    const receipt = { token: id(), at: Date.now(), id: options.submissionId || id(),
      widget: options.widget, path: target.pathname, warning: String(options.warning || '').slice(0, 600) };
    storage('setItem', prefix + receipt.token, JSON.stringify(receipt));
    storage('setItem', latestPrefix + receipt.widget, receipt.token);
    target.searchParams.set('contact_posted', 'true');
    target.searchParams.set('mvp_rfq_receipt', receipt.token);
    let field = returnField;
    if (!field) { field = document.createElement('input'); field.type = 'hidden'; field.name = 'return_to'; form.append(field); }
    field.value = target.pathname + target.search + target.hash;
    const state = { receipt, button: options.button, onRelease: options.onRelease };
    if (state.button) { triggers.set(state.button, form); state.button.setAttribute('aria-busy', 'true'); }
    form.setAttribute('aria-busy', 'true');
    state.timer = window.setTimeout(() => release(form), 30000);
    active.set(form, state);
    return { repeat: false, receipt };
  }
  function returned(options) {
    const query = new URLSearchParams(window.location.search);
    const token = query.get('mvp_rfq_receipt') || '';
    const receipt = token ? readReceipt(token) : null;
    if (!options.posted || options.errors || !receipt || receipt.widget !== options.widget ||
        storage('getItem', latestPrefix + receipt.widget) !== token ||
        receipt.path !== window.location.pathname || (options.submissionId && receipt.id !== options.submissionId)) return null;
    // Retain a non-personal receipt for redisplaying success; each conversion can
    // consume it only once. It expires with the rest of the session receipts.
    if (options.consume !== false) {
      if (receipt.consumed) return null;
      receipt.consumed = true;
      storage('setItem', prefix + token, JSON.stringify(receipt));
    }
    return receipt;
  }
  function suppressHumanRetry(event, form) {
    if (event.isTrusted && form && active.has(form)) { event.preventDefault(); event.stopImmediatePropagation(); }
  }
  window.addEventListener('click', event => {
    const button = event.target.closest && event.target.closest('button,input[type="submit"]');
    if (button) suppressHumanRetry(event, triggers.get(button) || (button.type === 'submit' ? button.form : null));
  }, true);
  window.addEventListener('keydown', event => {
    if (event.key === 'Enter') suppressHumanRetry(event, event.target.form);
  }, true);
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('form[aria-busy="true"]').forEach(form => release(form));
  });
  function fingerprint(payload) {
    const copy = Object.assign({}, payload);
    delete copy.submitted_at; delete copy.email_body;
    const text = JSON.stringify(copy);
    let hash = 2166136261;
    for (let i = 0; i < text.length; i++) hash = Math.imul(hash ^ text.charCodeAt(i), 16777619);
    return (hash >>> 0).toString(16);
  }
  function dispatchSheet(endpoint, payload, honeypotName, honeypotValue) {
    const key = (payload.submission_id || '') + ':' + fingerprint(payload);
    const previous = sheetAttempts.get(key);
    if (previous && Date.now() - previous.at < 30000) return previous.promise;
    const body = new URLSearchParams();
    body.set('rfq_payload_json', JSON.stringify(payload));
    body.set(honeypotName || 'company_website', honeypotValue || '');
    const notify = state => {
      window.dispatchEvent(new CustomEvent('mvp:rfq-intake-state', { detail: { submissionId: payload.submission_id, state } }));
      return { state };
    };
    const promise = !endpoint ? Promise.resolve(notify('unconfigured')) : Promise.resolve().then(() => window.fetch(endpoint, {
      method: 'POST', mode: 'no-cors', keepalive: true, body,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' }
    })).then(() => notify('unconfirmed'), error => {
      console.warn('RFQ intake receipt is unconfirmed; use the request ID to reconcile delivery.', error);
      return notify('unconfirmed');
    });
    sheetAttempts.set(key, { at: Date.now(), promise });
    return promise;
  }
  async function responseMessage(response, defaultMessage) {
    if (!response.ok) throw new Error('Request returned HTTP ' + response.status);
    const unconfirmed = () => { const error = new Error('The request receipt could not be confirmed.'); error.receiptUnconfirmed = true; return error; };
    const type = String(response.headers.get('content-type') || '').toLowerCase();
    if (type.includes('application/json')) {
      let data;
      try { data = await response.json(); } catch (_) { throw unconfirmed(); }
      if (!data || typeof data !== 'object' || Array.isArray(data)) throw unconfirmed();
      const status = String(data.status || data.result || '').toLowerCase();
      if (data.ok === false || (status && !['ok', 'success', 'received'].includes(status))) {
        throw new Error('The quote endpoint rejected the request.');
      }
      if (data.ok !== true && !['ok', 'success', 'received'].includes(status)) throw unconfirmed();
      return typeof data.message === 'string' && data.message.trim() && data.message.length <= 160 ? data.message.trim() : defaultMessage;
    }
    const text = (await response.text()).trim();
    if (!/^(ok|success|received)$/i.test(text)) throw unconfirmed();
    return defaultMessage;
  }
  window.mvpRfqSubmission = Object.freeze({ id, beginNative, returned, release, dispatchSheet, responseMessage });
})();
